import { normalizeQuestionCanvasData } from "./utils/questionFrames";
import Tutorial from "../tutorial/Tutorial";
import { isDemoQuestion, isTutorial } from "../tutorial/tutorialStorage";
import { workspaceStorage } from "../tutorial/tutorialStorage";
import Canvas from "../canvas/Canvas";
import StructurePanel from "../canvas/components/StructurePanel";
import Palette from "../palette/Palette";
import ConfirmationModal from "./components/ConfirmationModal";
import InformationTabs from "../informationTabs/InformationTabs";
import PanelToggleButtons from "./components/PanelToggleButtons";
import HelpIcon from "../shared/components/HelpIcon";
import styles from "./MemoryModelEditor.module.css";
import { useResponsivePanels } from "./hooks/useResponsivePanels";

import {
  useMemoryModelEditorState,
  clearCanvasStorage,
} from "./hooks/useMemoryModelEditorState";
import { loadInitialUIData, deleteQuestionCanvasData } from "./utils/localStorage";
import { useMemoryModelEditorRefs } from "./hooks/useRef";
import {
  useCanvasLocalStorage,
  useUILocalStorage,
} from "./hooks/useLocalStorage";
import { useCanvasSubmission } from "./hooks/useCanvasSubmission";
import { useUndoHistory } from "./hooks/useUndoHistory";
import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { CanvasElement, BoxTypeName } from "../shared/types";
import {
  createMasterErrorList,
  MasterErrorList,
} from "./utils/masterErrorList";
import { spreadOverlappingElements } from "../canvas/utils/boundary.helpers";

// Layout constants
const MAX_INFO_PANEL_VIEWPORT_RATIO = 0.6667;
const MIN_INFO_PANEL_WIDTH = 260;
const MIN_CANVAS_COLUMN_WIDTH = 780;
const INFO_RESIZE_DIVIDER_WIDTH = 8;
const MIN_PALETTE_WIDTH = 200;
const MAX_PALETTE_WIDTH = 400;
const DEFAULT_PALETTE_WIDTH = 280;
const SNAP_CLOSE_THRESHOLD = 100;
const STRUCTURE_PANEL_RESIZE_HANDLE_HEIGHT = 8;

interface MemoryModelEditorProps {
  sandbox?: boolean;
}

export default function MemoryModelEditor({
  sandbox = true,
}: MemoryModelEditorProps) {
  const state = useMemoryModelEditorState(sandbox);
  const refs = useMemoryModelEditorRefs();
  const [openEditor, setOpenEditor] = useState<
    ((element: CanvasElement) => void) | null
  >(null);

  const [paletteWidth, setPaletteWidth] = useState<number>(
    DEFAULT_PALETTE_WIDTH
  );
  const [isResizingPalette, setIsResizingPalette] = useState<boolean>(false);
  const [tempPaletteWidth, setTempPaletteWidth] = useState<number>(
    DEFAULT_PALETTE_WIDTH
  );
  const [maxInfoPanelWidth, setMaxInfoPanelWidth] = useState<number>(
    window.innerWidth * MAX_INFO_PANEL_VIEWPORT_RATIO
  );

  const [tutorialCheckedModel, setTutorialCheckedModel] = useState<string | null>(null);
  const tutorialModelKey = JSON.stringify(state.elements.map(({id, kind, invalidated}) => ({id, kind, invalidated})));
  // A tutorial result only describes the model that was actually submitted.
  useEffect(() => {
    if (isTutorial() && tutorialCheckedModel !== null && tutorialCheckedModel !== tutorialModelKey) {
      state.setSubmissionResults(null);
      setTutorialCheckedModel(null);
    }
  }, [tutorialModelKey, tutorialCheckedModel, state.setSubmissionResults]);
  const [currentQuestionData, setCurrentQuestionData] = useState<any>(null);
  const _initialUI = loadInitialUIData();
  const [canvasScale, setCanvasScale] = useState<number>(_initialUI.canvasScale ?? 1);
  const [editorScale, setEditorScale] = useState<number>(_initialUI.editorScale ?? 1);
  const [fontScale, setFontScale] = useState<number>(() => {
    const saved = workspaceStorage.getItem("questionFontScale");
    return saved ? parseFloat(saved) : 1;
  });
  const adjustFontScale = (delta: number) => {
    setFontScale((prev) => {
      const next = Math.max(0.75, Math.min(1.5, Math.round((prev + delta) * 10) / 10));
      workspaceStorage.setItem("questionFontScale", String(next));
      return next;
    });
  };

  // Initialize undo history
  const { canUndo, canRedo, undo, redo, recordState, clearHistory } = useUndoHistory(
    state.setElements,
    state.setElementIds,
    state.setElementClasses
  );

  // Track previous state for recording changes
  const prevStateRef = useRef({
    elements: state.elements,
    ids: state.elementIds,
    classes: state.elementClasses,
  });

  const handleEditorOpenerReady = useCallback(
    (opener: (element: CanvasElement) => void) => {
      setOpenEditor(() => opener);
    },
    []
  );

  // Record state changes for undo functionality
  useEffect(() => {
    const prevState = prevStateRef.current;
    const currentState = {
      elements: state.elements,
      ids: state.elementIds,
      classes: state.elementClasses,
    };

    // Check if state has actually changed
    const stripTransient = (els: CanvasElement[]) =>
      els.map(({ color, errors, ...rest }) => rest);
    
    const hasChanged =
      JSON.stringify(stripTransient(prevState.elements)) !== JSON.stringify(stripTransient(currentState.elements)) ||
      JSON.stringify(prevState.ids) !== JSON.stringify(currentState.ids) ||
      JSON.stringify(prevState.classes) !== JSON.stringify(currentState.classes);

    if (hasChanged) {
      // Record the current state (after the change)
      recordState(currentState);
      prevStateRef.current = currentState;
    }
  }, [state.elements, state.elementIds, state.elementClasses, recordState]);

  const clearCanvas = () => {
    const emptyElements = isTutorial() ? normalizeQuestionCanvasData().elements : [];
    if (state.selectedQuestionIndex !== null && state.selectedQuestionType !== null) {
      deleteQuestionCanvasData(state.selectedQuestionType, state.selectedQuestionIndex);
    }
    state.setElements(emptyElements);
    state.setElementIds([]);
    state.setElementClasses([]);
    state.setSelectedQuestionIndex(null);
    state.setSelectedQuestionType(null);
    state.setSubmissionResults(null);
    state.setActiveInfoTab("question");
    state.setCanvasResetKey((prev) => prev + 1);
    clearCanvasStorage();
    const baselineState = {
      elements: emptyElements,
      ids: [],
      classes: [],
    };
    
    prevStateRef.current = baselineState;
    clearHistory(baselineState);
  };

  const restoreCanvas = (
    elements: CanvasElement[],
    ids: number[],
    classes: string[]
  ) => {
    const restoredElements = spreadOverlappingElements(elements);

    const baselineState = {
      elements: restoredElements,
      ids,
      classes,
    };

    state.setElements(restoredElements);
    state.setElementIds(ids);
    state.setElementClasses(classes);

    prevStateRef.current = baselineState;
    clearHistory(baselineState);
  };

  const effectiveSandboxMode = state.isSandboxMode || state.selectedQuestionType === "experiment";

  // When a question loads in practice mode, seed elementClasses with the class names
  // from the question's answer so the class selector shows them as pre-built options.
  useEffect(() => {
    if (!effectiveSandboxMode || !currentQuestionData) return;
    const questionClassNames = getQuestionClassNames(currentQuestionData);
    if (questionClassNames.length === 0) return;
    state.setElementClasses((prev) => {
      const merged = [...prev];
      for (const name of questionClassNames) {
        if (!merged.includes(name)) {
          merged.push(name);
        }
      }
      return merged;
    });
  }, [currentQuestionData, effectiveSandboxMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const masterErrorList: MasterErrorList = useMemo(
    () => createMasterErrorList(state.elements),
    [state.elements]
  );

  const addElementId = (id: number): void => {
    state.setElementIds((prevIds) => {
      const insertIndex = prevIds.findIndex((existingId) => existingId > id);
      return insertIndex === -1
        ? [...prevIds, id]
        : [...prevIds.slice(0, insertIndex), id, ...prevIds.slice(insertIndex)];
    });
  };

  const removeElementId = (id: number | "_"): void => {
    if (typeof id === "number") {
      state.setElementIds((prevIds) =>
        prevIds.filter((existingId) => existingId !== id)
      );
    }
  };

  const addElementClass = (className: string): void => {
    state.setElementClasses((prevClasses) => {
      if (prevClasses.includes(className)) return prevClasses;
      const insertIndex = prevClasses.findIndex(
        (existingClass) => existingClass.localeCompare(className) > 0
      );
      return insertIndex === -1
        ? [...prevClasses, className]
        : [
            ...prevClasses.slice(0, insertIndex),
            className,
            ...prevClasses.slice(insertIndex),
          ];
    });
  };

  const removeElementClass = (className: string): void => {
    state.setElementClasses((prevClasses) =>
      prevClasses.filter((existingClass) => existingClass !== className)
    );
  };

  const getQuestionFunctionNames = useCallback((questionData: any): string[] => {
    const seen = new Set<string>();
    const collectFrameNames = (boxes: any[]) => {
      for (const box of boxes) {
        if (box.type === ".frame" && typeof box.name === "string") {
          seen.add(box.name);
        }
      }
    };
    if (Array.isArray(questionData?.answer)) {
      collectFrameNames(questionData.answer);
    }
    if (Array.isArray(questionData?.steps)) {
      for (const step of questionData.steps) {
        if (Array.isArray(step.answer)) {
          collectFrameNames(step.answer);
        }
      }
    }
    return Array.from(seen);
  }, []);

  const getQuestionClassNames = useCallback((questionData: any): string[] => {
    if (!questionData?.answer || !Array.isArray(questionData.answer)) {
      return [];
    }
    const seen = new Set<string>();
    const names: string[] = [];
    for (const box of questionData.answer) {
      if (
        (box.type === ".class" || box.type === "object") &&
        typeof box.name === "string"
      ) {
        if (!seen.has(box.name)) {
          seen.add(box.name);
          names.push(box.name);
        }
      }
    }
    return names;
  }, []);

  const getRequiredBoxTypeNames = useCallback((questionData: any): BoxTypeName[] => {
    if (!questionData?.answer || !Array.isArray(questionData.answer)) {
      return [];
    }

    const requiredTypes = new Set<BoxTypeName>();

    const hasFrames = questionData.answer.some(
      (box: any) => box.type === ".frame"
    );
    if (hasFrames) {
      requiredTypes.add("function" as BoxTypeName);
    }

    questionData.answer.forEach((box: any) => {
      const boxType = box.type;

      switch (boxType) {
        case ".frame":
          break;
        case "int":
          requiredTypes.add("int" as BoxTypeName);
          break;
        case "float":
          requiredTypes.add("float" as BoxTypeName);
          break;
        case "str":
          requiredTypes.add("str" as BoxTypeName);
          break;
        case "bool":
          requiredTypes.add("bool" as BoxTypeName);
          break;
        case "NoneType":
        case "None":
          requiredTypes.add("none" as BoxTypeName);
          break;
        case "list":
          requiredTypes.add("list" as BoxTypeName);
          break;
        case "tuple":
          requiredTypes.add("tuple" as BoxTypeName);
          break;
        case "set":
          requiredTypes.add("set" as BoxTypeName);
          break;
        case "dict":
          requiredTypes.add("dict" as BoxTypeName);
          break;
        case ".class":
        case "object":
          requiredTypes.add("class" as BoxTypeName);
          break;
      }
    });

    return Array.from(requiredTypes);
  }, []);

  const { handleCanvasSubmit, handleCanvasSubmitAtLine } = useCanvasSubmission({
    selectedQuestionIndex: state.selectedQuestionIndex,
    selectedQuestionType: state.selectedQuestionType,
    elements: state.elements,
    setElements: state.setElements,
    setSubmissionResults: state.setSubmissionResults,
  });

  useCanvasLocalStorage({
    elements: state.elements,
    ids: state.elementIds,
    classes: state.elementClasses,
  });

  useUILocalStorage({
    activeTab: state.activeInfoTab,
    questionIndex: state.selectedQuestionIndex,
    questionType: state.selectedQuestionType,
    submissionResults: state.submissionResults,
    sandboxMode: state.isSandboxMode,
    visualStyle: state.visualStyle,
    pythonTutorReferenceArrows: state.pythonTutorReferenceArrows,
    pythonTutorStandalonePrimitives: state.pythonTutorStandalonePrimitives,
    showLinkedListView: state.showLinkedListView,
    structurePanelCollapsed: state.structurePanelCollapsed,
    structurePanelHeight: state.structurePanelHeight,
    questionView: state.questionView,
    isInfoPanelOpen: state.isInfoPanelOpen,
    canvasScale,
    editorScale,
  });

  useResponsivePanels({
    enabled: true,
    isPaletteOpen: state.isPaletteOpen,
    isInfoPanelOpen: state.isInfoPanelOpen,
    setIsPaletteOpen: state.setIsPaletteOpen,
    setIsInfoPanelOpen: state.setIsInfoPanelOpen,
  });

  const currentCanvasState = useMemo(
    () => ({
      elements: state.elements,
      ids: state.elementIds,
      classes: state.elementClasses,
    }),
    [state.elements, state.elementIds, state.elementClasses]
  );

  useEffect(() => {
    if (!isResizingPalette) return;

    let animationFrameId: number;
    let lastUpdateTime = 0;
    const UPDATE_INTERVAL = 50;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(0, Math.min(MAX_PALETTE_WIDTH, e.clientX));

      setTempPaletteWidth(newWidth);

      const now = Date.now();
      if (now - lastUpdateTime >= UPDATE_INTERVAL) {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = requestAnimationFrame(() => {
          setPaletteWidth(newWidth);
          lastUpdateTime = now;
        });
      }
    };

    const handleMouseUp = () => {
      if (tempPaletteWidth < SNAP_CLOSE_THRESHOLD) {
        state.setIsPaletteOpen(false);
        setPaletteWidth(DEFAULT_PALETTE_WIDTH);
        setTempPaletteWidth(DEFAULT_PALETTE_WIDTH);
      } else {
        const finalWidth = Math.max(MIN_PALETTE_WIDTH, tempPaletteWidth);
        setPaletteWidth(finalWidth);
        setTempPaletteWidth(finalWidth);
      }

      setIsResizingPalette(false);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isResizingPalette, tempPaletteWidth, state]);

  // Keep stable refs for info panel resize handlers so the effect
  // only re-runs when isResizingInfoPanel changes (not on every render).
  const infoPanelSetWidth = state.setInfoPanelWidth;
  const infoPanelSetResizing = state.setIsResizingInfoPanel;
  const infoPanelSetOpen = state.setIsInfoPanelOpen;
  const mainContainerRefCurrent = refs.mainContainerRef;

  useEffect(() => {
    if (!state.isResizingInfoPanel) return;

    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const handleMouseMove = (event: MouseEvent) => {
      if (!mainContainerRefCurrent.current) return;

      const containerRect =
        mainContainerRefCurrent.current.getBoundingClientRect();
      const newWidth = containerRect.right - event.clientX;

      const maxWidthBasedOnViewport =
        window.innerWidth * MAX_INFO_PANEL_VIEWPORT_RATIO;
      const maxWidthPreservingCanvas = Math.max(
        MIN_INFO_PANEL_WIDTH,
        containerRect.width - MIN_CANVAS_COLUMN_WIDTH - INFO_RESIZE_DIVIDER_WIDTH
      );
      const maxAllowedWidth = Math.min(
        maxWidthPreservingCanvas,
        maxWidthBasedOnViewport
      );

      const clamped = Math.max(0, Math.min(newWidth, maxAllowedWidth));
      infoPanelSetWidth(clamped);
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (mainContainerRefCurrent.current) {
        const containerRect =
          mainContainerRefCurrent.current.getBoundingClientRect();
        const finalWidth = containerRect.right - event.clientX;
        const maxWidthBasedOnViewport =
          window.innerWidth * MAX_INFO_PANEL_VIEWPORT_RATIO;
        const maxWidthPreservingCanvas = Math.max(
          MIN_INFO_PANEL_WIDTH,
          containerRect.width - MIN_CANVAS_COLUMN_WIDTH - INFO_RESIZE_DIVIDER_WIDTH
        );
        const maxAllowedWidth = Math.min(
          maxWidthPreservingCanvas,
          maxWidthBasedOnViewport
        );

        if (finalWidth < SNAP_CLOSE_THRESHOLD) {
          infoPanelSetOpen(false);
          infoPanelSetWidth(500);
        } else {
          const settledWidth = Math.max(
            MIN_INFO_PANEL_WIDTH,
            Math.min(finalWidth, maxAllowedWidth)
          );
          infoPanelSetWidth(settledWidth);
        }
      }

      infoPanelSetResizing(false);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [state.isResizingInfoPanel, infoPanelSetWidth, infoPanelSetResizing, infoPanelSetOpen, mainContainerRefCurrent]);

  useEffect(() => {
    const container = refs.mainContainerRef.current;
    if (!container) return;

    const updateMaxInfoPanelWidth = () => {
      const containerWidth = container.getBoundingClientRect().width;
      const maxWidthBasedOnViewport =
        window.innerWidth * MAX_INFO_PANEL_VIEWPORT_RATIO;
      const maxWidthPreservingCanvas = Math.max(
        MIN_INFO_PANEL_WIDTH,
        containerWidth - MIN_CANVAS_COLUMN_WIDTH - INFO_RESIZE_DIVIDER_WIDTH
      );
      const nextMax = Math.min(
        maxWidthBasedOnViewport,
        maxWidthPreservingCanvas
      );

      setMaxInfoPanelWidth(nextMax);
      state.setInfoPanelWidth((prev) => Math.min(prev, nextMax));
    };

    updateMaxInfoPanelWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateMaxInfoPanelWidth();
    });

    resizeObserver.observe(container);
    window.addEventListener("resize", updateMaxInfoPanelWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateMaxInfoPanelWidth);
    };
  }, [refs.mainContainerRef, state.setInfoPanelWidth]);

  return (
    <div className={`${styles.editorContainer} ${isTutorial() ? "tutorial-workspace" : ""}`}>
      <PanelToggleButtons
        isPaletteOpen={state.isPaletteOpen}
        isInfoPanelOpen={state.isInfoPanelOpen}
        onTogglePalette={() => state.setIsPaletteOpen((prev) => !prev)}
        onToggleInfoPanel={() => state.setIsInfoPanelOpen((prev) => !prev)}
      />

      {state.isPaletteOpen && (
        <>
          <div
            className={`${styles.palettePanel} ${
              isResizingPalette ? styles.resizing : ""
            }`}
            style={{
              width: `${isResizingPalette ? tempPaletteWidth : paletteWidth}px`,
              minWidth: `${
                isResizingPalette ? tempPaletteWidth : paletteWidth
              }px`,
            }}
          >
            <div
              data-tour="palette" className={styles.paletteContent}
              style={{
                width: `${paletteWidth}px`,
              }}
            >
              <Palette
                activeTab={state.activePaletteTab}
                setActive={state.setActivePaletteTab}
                requiredBoxes={
                  effectiveSandboxMode && currentQuestionData
                    ? getRequiredBoxTypeNames(currentQuestionData).filter(
                        (type) => !(state.selectedQuestionType === "experiment" && type === "function")
                      )
                    : undefined
                }
                isPracticeMode={effectiveSandboxMode}
                isSandboxMode={effectiveSandboxMode}
                onModeToggle={isTutorial() || state.selectedQuestionType === "experiment" ? undefined : () => state.setShowModeToggleModal(true)}
                onClear={() => state.setShowClearCanvasModal(true)}
                onUndo={undo}
                onRedo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
                elements={state.elements}
                scale={canvasScale}
                onScaleChange={setCanvasScale}
                editorScale={editorScale}
                onEditorScaleChange={setEditorScale}
                visualStyle={state.visualStyle}
                onVisualStyleChange={state.setVisualStyle}
                pythonTutorReferenceArrows={state.pythonTutorReferenceArrows}
                onPythonTutorReferenceArrowsChange={
                  state.setPythonTutorReferenceArrows
                }
                pythonTutorStandalonePrimitives={
                  state.pythonTutorStandalonePrimitives
                }
                onPythonTutorStandalonePrimitivesChange={
                  state.setPythonTutorStandalonePrimitives
                }
                showLinkedListView={state.showLinkedListView}
                onShowLinkedListViewChange={state.setShowLinkedListView}
                fontScale={fontScale}
                onFontScaleChange={adjustFontScale}
              />
            </div>
          </div>

          <div
            className={styles.resizeDivider}
            onMouseDown={() => setIsResizingPalette(true)}
          />
        </>
      )}

      <div data-tour="main-container" ref={refs.mainContainerRef} className={styles.mainContainer}>
        <div data-tour="canvas-column" className={styles.canvasColumn}>
          <div data-tour="canvas" className={styles.canvasArea}>
            <Canvas
              key={state.canvasResetKey}
              elements={state.elements}
              setElements={state.setElements}
              ids={state.elementIds}
              addId={addElementId}
              removeId={removeElementId}
              classes={state.elementClasses}
              addClasses={addElementClass}
              removeClasses={removeElementClass}
              sandbox={!effectiveSandboxMode}
              canManageClasses={!effectiveSandboxMode || state.selectedQuestionIndex === null}
              canManageFunctions={!effectiveSandboxMode || state.selectedQuestionIndex === null}
              onClear={() => state.setShowClearCanvasModal(true)}
              onEditorOpenerReady={handleEditorOpenerReady}
              scale={canvasScale}
              onScaleChange={setCanvasScale}
              editorScale={editorScale}
              visualStyle={state.visualStyle}
              pythonTutorReferenceArrows={state.pythonTutorReferenceArrows}
              pythonTutorStandalonePrimitives={
                state.pythonTutorStandalonePrimitives
              }
              questionFunctionNames={
                effectiveSandboxMode && currentQuestionData
                  ? getQuestionFunctionNames(currentQuestionData)
                  : undefined
              }
              isQuestionMode={isTutorial() || state.selectedQuestionIndex !== null}
              preserveCollapsedWorkspace={state.showLinkedListView}
              workspaceHeightOffset={
                state.showLinkedListView && !state.structurePanelCollapsed
                  ? state.structurePanelHeight +
                    STRUCTURE_PANEL_RESIZE_HANDLE_HEIGHT
                  : 0
              }
            />
            <div className={styles.canvasHelp}>
              <HelpIcon
                title="Canvas"
                text="This is your workspace for building the memory model. Drag boxes in from the Palette, click a box to edit its values, and set a reference by picking another box's id in the field editor."
              />
            </div>
          </div>

          <StructurePanel
            enabled={state.showLinkedListView}
            elements={state.elements}
            collapsed={state.structurePanelCollapsed}
            height={state.structurePanelHeight}
            onCollapsedChange={state.setStructurePanelCollapsed}
            onHeightChange={state.setStructurePanelHeight}
          />

          {state.jsonOutput && (
            <pre className={styles.jsonPreview}>{state.jsonOutput}</pre>
          )}
        </div>

        {state.isInfoPanelOpen && (
          <>
            <div
              className={styles.resizeDivider}
              onMouseDown={() => state.setIsResizingInfoPanel(true)}
            />

            <div
              data-tour="info-panel" className={`${styles.infoPanel} ${
                state.isResizingInfoPanel ? styles.noTransition : ""
              }`}
              style={{
                width: `${Math.min(state.infoPanelWidth, maxInfoPanelWidth)}px`,
                minWidth: state.isResizingInfoPanel
                  ? "0px"
                  : `${Math.min(MIN_INFO_PANEL_WIDTH, maxInfoPanelWidth)}px`,
                maxWidth: `${Math.min(
                  maxInfoPanelWidth,
                  window.innerWidth * MAX_INFO_PANEL_VIEWPORT_RATIO
                )}px`,
              }}
            >
              <InformationTabs
                linkedQuestion={state.linkedQuestion}
                onQuestionLinkConsumed={() => state.setLinkedQuestion(null)}
                submissionResults={state.submissionResults}
                questionSelected={state.selectedQuestionIndex !== null}
                questionIndex={state.selectedQuestionIndex}
                setQuestionIndex={state.setSelectedQuestionIndex}
                questionType={state.selectedQuestionType}
                setQuestionType={state.setSelectedQuestionType}
                questionView={state.questionView}
                setQuestionView={state.setQuestionView}
                onSubmit={isTutorial() ? async () => {
                  const checkedKey = tutorialModelKey;
                  setTutorialCheckedModel(null);
                  const correct = await handleCanvasSubmit();
                  setTutorialCheckedModel(checkedKey);
                  return correct;
                } : handleCanvasSubmit}
                onSubmitAtLine={isTutorial() ? async (line, iteration) => {
                  setTutorialCheckedModel(null);
                  return handleCanvasSubmitAtLine(line, iteration);
                } : handleCanvasSubmitAtLine}
                setSubmissionResults={state.setSubmissionResults}
                onClearCanvas={clearCanvas}
                onRestoreCanvas={restoreCanvas}
                currentCanvasState={currentCanvasState}
                masterErrorList={masterErrorList}
                elements={state.elements}
                setElements={state.setElements}
                onOpenEditor={openEditor || (() => {})}
                isSandboxMode={state.isSandboxMode}
                onQuestionDataChange={setCurrentQuestionData}
                tabScrollPositions={state.tabScrollPositions}
                setTabScrollPositions={state.setTabScrollPositions}
                fontScale={fontScale}
              />
            </div>
          </>
        )}
      </div>

      <Tutorial submissionFailed={tutorialCheckedModel === tutorialModelKey && state.submissionResults?.correct === false} demoActive={isDemoQuestion(state.selectedQuestionIndex, state.selectedQuestionType, state.questionView)} elements={state.elements} correct={tutorialCheckedModel === tutorialModelKey && state.submissionResults?.correct === true} />

      {state.showClearCanvasModal && (
        <ConfirmationModal
          title="Clear Canvas?"
          message="This will clear the entire canvas and cannot be undone."
          confirmLabel="Clear"
          cancelLabel="Cancel"
          onConfirm={() => {
            clearCanvas();
            state.setShowClearCanvasModal(false);
          }}
          onCancel={() => state.setShowClearCanvasModal(false)}
        />
      )}

      {state.showModeToggleModal && (
        <ConfirmationModal
          title="Switch Mode?"
          message="Switching modes will clear the canvas. Your current work will be saved and restored if you return to this question."
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          onConfirm={() => {
            clearCanvas();
            state.setIsSandboxMode((prev) => !prev);
            state.setShowModeToggleModal(false);
          }}
          onCancel={() => state.setShowModeToggleModal(false)}
        />
      )}

    </div>
  );
}
